import { Link } from "react-router-dom";
import { ArrowRight, Monitor, MapPin, Users2, FlaskConical, Sparkles, Lightbulb, PenTool, Target, TrendingUp, CheckCircle } from "lucide-react";

export default function Services() {
  return (
    <div className="overflow-x-hidden" style={{ backgroundColor: '#FFF8F3' }}>
      {/* ── HERO ── */}
      <section data-testid="services-hero-section" className="pt-32 pb-20"
        style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-5" style={{ color: '#D4EC8E' }}>Training Services</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How Technofocus<br />
            <span style={{ color: '#D4EC8E', fontWeight: 600 }}>Delivers Learning</span>
          </h1>
          <p className="text-lg max-w-2xl mx-auto mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
            From live virtual sessions to in-person events, private programs to hackathons — we design the right learning experience for your audience and goals.
          </p>
          <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noopener noreferrer"
            data-testid="services-hero-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors"
            style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#BFD872'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4EC8E'}>
            Browse Courses on LevelUp <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ── HOW WE DELIVER ── */}
      <section data-testid="delivery-section" className="py-20 md:py-28" style={{ backgroundColor: '#FFF8F3' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Training Delivery</p>
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Every Format. Every Audience.</h2>
            <p className="text-gray-500 max-w-xl mx-auto">From live virtual classrooms to immersive on-site workshops — we design the right learning experience for your teams, your context, and your goals.</p>
          </div>

          {/* Delivery Format Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {[
              {
                icon: Monitor, title: "Virtual Online Live Training",
                desc: "Expert-led, live virtual sessions delivered globally. Includes real-time Q&A, breakout rooms, whiteboard collaboration, and structured labs. Perfect for distributed teams.",
                color: "#194449", bg: "#E5F3F0",
              },
              {
                icon: MapPin, title: "In-Person Events & Workshops",
                desc: "Immersive on-site or venue-based training for teams that benefit from face-to-face facilitation. We handle logistics, venue, catering, and all event coordination.",
                color: "#07641D", bg: "#F2FBDB",
              },
              {
                icon: Users2, title: "Private Partner Training",
                desc: "Dedicated programs built exclusively for your organization. Custom content, your branding, your schedule — delivered at scale with full reporting for your leadership.",
                color: "#0B2E38", bg: "#E5F3F0",
              },
              {
                icon: FlaskConical, title: "Hands-On Labs & Hackathons",
                desc: "Carefully tailored lab environments and hackathon formats where participants solve real-world Microsoft technology challenges. Ideal for technical upskilling and team engagement.",
                color: "#194449", bg: "#E5F3F0",
              },
              {
                icon: Sparkles, title: "AI Conferences for Partners",
                desc: "We deliver and host Microsoft AI Tour events and large-scale Microsoft technology conferences for partners — combining keynotes, demos, breakouts, and networking in one experience.",
                color: "#07641D", bg: "#F2FBDB",
              },
              {
                icon: Lightbulb, title: "Scenario-Based Custom Learning",
                desc: "Advanced, role-specific learning journeys built around your unique customer scenarios. Combine theory, guided practice, and simulated real-world challenges for lasting impact.",
                color: "#0B2E38", bg: "#E5F3F0",
              },
            ].map((format, i) => (
              <div key={i} data-testid={`delivery-format-${i}`}
                className="bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                style={{ borderColor: '#E2EBE9' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: format.bg }}>
                  <format.icon size={20} style={{ color: format.color }} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{format.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{format.desc}</p>
              </div>
            ))}
          </div>

          {/* AI Conference Gallery — 4 photos */}
          <div className="rounded-3xl overflow-hidden border mb-16" style={{ borderColor: '#E2EBE9' }}>
            <div className="p-7 bg-white border-b" style={{ borderColor: '#E2EBE9' }}>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D4EC8E' }}></div>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#194449' }}>From the Floor — Events We've Delivered</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                { src: "/ai-conference-1.jpeg", label: "Microsoft AI Tour for Partners — Paris", sub: "Partner Summit" },
                { src: "/event-lab-session.jpeg", label: "Microsoft Ignite", sub: "Technical Workshop" },
                { src: "/team-ai-tour.jpeg", label: "Microsoft AI Tour for Partners — London", sub: "Partner Conference" },
                { src: "/ai-conference-2.jpeg", label: "Microsoft AI Tour for Partners — Seoul", sub: "AI Conference" },
              ].map((photo, i) => (
                <div key={i} className="relative overflow-hidden" style={{ height: 280 }}>
                  <img src={photo.src} alt={photo.label} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(9,31,44,0.80) 0%, transparent 55%)' }}></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#D4EC8E' }}>{photo.sub}</p>
                    <p className="font-semibold text-sm leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{photo.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTENT & ENABLEMENT SERVICES ── */}
      <section data-testid="enablement-section" className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Content & Consulting</p>
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Beyond Courses — Full Enablement</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We don't just deliver training. We partner with you to build content, plan enablement strategy, and drive Microsoft technology adoption across your organization.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {[
              {
                icon: PenTool, title: "Custom Content Build",
                desc: "End-to-end content creation for sales, presales, and technical audiences. We curate and build courses, learning paths, facilitator guides, lab instructions, and scenario walkthroughs tailored to your solution areas and customer segments.",
                highlights: ["Sales enablement decks & e-learning", "Presales technical scenario scripts", "Lab guides & facilitator kits", "Localized multi-language content"],
                color: "#194449",
              },
              {
                icon: Target, title: "Enablement Planning",
                desc: "Structured skilling roadmaps that align your partner or enterprise teams with Microsoft technology priorities. We map roles to learning journeys, define success metrics, and manage the full training lifecycle.",
                highlights: ["Role-based learning path design", "Skilling calendar & milestone planning", "Partner readiness assessments", "Reporting & completion tracking"],
                color: "#07641D",
              },
              {
                icon: TrendingUp, title: "Driving Microsoft Technology Adoption",
                desc: "Strategic adoption programs that move organizations from awareness to active use of Microsoft cloud technologies. We combine targeted training, change management support, and executive briefings to accelerate time-to-value.",
                highlights: ["Enterprise adoption strategy", "Change management workshops", "Executive briefings & showcases", "Usage analytics & adoption reporting"],
                color: "#0B2E38",
              },
            ].map((svc, i) => (
              <div key={i} data-testid={`enablement-card-${i}`}
                className="rounded-3xl p-8 border hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: '#E2EBE9', backgroundColor: i % 2 === 1 ? '#FFF8F3' : 'white' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: svc.color + '12' }}>
                  <svc.icon size={22} style={{ color: svc.color }} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{svc.desc}</p>
                <ul className="space-y-2">
                  {svc.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle size={13} className="shrink-0 mt-0.5" style={{ color: svc.color }} />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section data-testid="services-cta-section" style={{ background: 'linear-gradient(135deg, #091F2C, #194449)' }} className="py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Start Learning?</h2>
          <p className="mb-8 max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>Access the full course catalog on LevelUp, or speak to our team to build a custom training program for your organization.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noopener noreferrer"
              data-testid="services-levelup-cta"
              className="px-8 py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-2"
              style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#BFD872'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4EC8E'}>
              Explore LevelUp <ArrowRight size={16} />
            </a>
            <Link to="/contact" data-testid="services-contact-cta"
              className="border text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold transition-colors"
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}>
              Talk to Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
